// Pure module — no Angular / Firebase / Capacitor imports — so the allow-list
// can be unit-tested standalone with a localStorage shim. See the matching
// .test.ts file for round-trip coverage of the wipe semantics.

// localStorage keys that must survive signOut. anything not in this list (and
// not matching a preserved prefix) is wiped on signOut so user-scoped state
// cannot leak into the next account on the same browser. See #3316 for why
// the previous opt-in (one removeItem per key) approach didn't scale.
export const DEVICE_LEVEL_LOCALSTORAGE_KEYS: readonly string[] = [
  // iOS one-time cleanup marker: the boot that deleted the `_pouch_*` IndexedDB
  // databases the pre-SQLite storage engine left behind wrote this. It survives
  // signOut so the cleanup does not run again on every account switch; the
  // sign-out path drops it deliberately once those leftovers are really gone,
  // and this list must not be the thing that destroys that decision. It does NOT
  // gate a migration and there is no data to lose by clearing it — an older
  // version of this comment claimed otherwise and blocked a repair for months.
  // See `design-docs/local-storage-and-auto-plan-cache.md`, "Re-arming the
  // cleanup (#4834)".
  //
  // literal must match SQLITE_MIGRATION_DONE_KEY in legacy-idb-cleanup.ts (the
  // matching test cross-checks the two). Not imported, because this module is
  // deliberately free of every dependency so the allow-list can be unit-tested
  // standalone.
  'sqlite_migration_idb_cleanup_done_v2',
  // Capacitor install referrer was already pulled once; harmless to re-run
  // but no point.
  'installReferrerChecked',
  // pre-login UTM attribution captured before any signup; survives so signup
  // tracking is consistent across logout/login cycles in the same browser.
  'cachedUtms',
  // the startup install sheet's "Don't Show Again" choice (#5707). it belongs to
  // the browser, not the account, so a sign-out must not bring the sheet back.
  // literal must match APP_INSTALL_STARTUP_OPT_OUT_KEY in app-install-resolver.ts
  // (the matching test cross-checks the two).
  'appInstallStartupSheetOptOut',
  // the developer opt-in that turns telemetry on for a LOCAL build. device-level,
  // never user-level, and it has to survive signOut: signing out is exactly what
  // somebody testing telemetry does, and losing the flag mid-test would silently
  // stop the events they are watching for, with nothing saying why. literal must
  // match TELEMETRY_LOCAL_OPT_IN_KEY in telemetry-mode.ts (the matching test
  // cross-checks the two).
  'cs_enable_telemetry',
  // multi-tab focus coordination — device-level, not user-level.
  'lastFocusedTabId',
  // stable per-install device identifier sent with FCM token registration so the
  // server can replace this device's old token entry instead of appending (#3714
  // duplicate-push fix). identifies the device installation, not the user; wiping
  // it would make the next registration after sign-out append a duplicate token
  // entry for this same device. value of PUSH_DEVICE_ID_KEY below.
  'pushDeviceId',
  // stable per-browser anonymous id used to bucket a logged-out purchase-first
  // buyer for the Paddle rollout ramp (#3910). identifies the browser, not the
  // user, so a signOut wipe must NOT reset it — else the same buyer could get a
  // different provider after a logout/login cycle. literal must match
  // PADDLE_BUCKET_ID_KEY in paddle-bucket-id.ts (the matching test cross-checks).
  'cs-paddle-bucket-id',
  // device-scoped UI language (a boot-time cache of the last-applied language on
  // this device, #4147). survives signOut so the login / onboarding screens
  // render in the chosen language and the i18n module can read it synchronously
  // at boot. it is a per-device property, not per-user — the authoritative
  // per-user preference is settings.uiLanguage, which wins on sign-in via the
  // reconciliation. literal must match CS_UI_LANGUAGE_KEY in
  // ../i18n/ui-language.ts (the matching test cross-checks the two).
  'cs_ui_language',
  // WHO chose the device-scoped UI language above. Survives signOut for exactly
  // the same reason the language does — and it MUST, because the case it exists
  // to serve is "user A signed out, user B signs in": a key wiped on sign-out
  // would be gone precisely when the reconciliation needs it, and every language
  // would look like an unowned leftover again. literal must match
  // CS_UI_LANGUAGE_UID_KEY in ../i18n/ui-language.ts (the matching test
  // cross-checks the two).
  'cs_ui_language_uid',
  // A language chosen on this device that the settings doc has not confirmed yet.
  // Survives signOut with the other two: a sign-out between the switch and the
  // confirming sync would otherwise strand a half-applied choice. literal must
  // match CS_UI_LANGUAGE_UNCONFIRMED_KEY in ../i18n/ui-language.ts (the matching
  // test cross-checks the two).
  'cs_ui_language_unconfirmed',
  // WHICH ENGINE this device's local PouchDB data is actually in (#4981 phase 2).
  // A device-level fact, never user-scoped, so it must survive signOut: without it
  // a migrated device leaves the cohort at every sign-in cycle and re-copies its
  // whole database. literal must match LOCAL_DATABASE_ENGINE_KEY in
  // local-database-engine-decisions.ts (the matching test cross-checks the two).
  // Not imported, because this module is deliberately free of every dependency.
  'cs_local_database_engine',
  // WHICH SQLITE FILES a background copy has written into (#4981 phase 3, #5449). A JSON
  // array of logical database names. It has to survive signOut because it is the ONLY
  // thing on the device that can name those files: the database provider never held them,
  // and the native plugin has no "list the databases" call. Sign-out reads this note and
  // deletes what it names.
  //
  // It looks user-scoped and is not. The value names a particular account's databases, but
  // the fact it records — "this device is carrying files named X" — is a fact about the
  // device, exactly like the iOS cleanup marker at the top of this list. And it is not kept
  // for ever: the sign-out teardown's SQLite block removes it once those files are really
  // gone, and keeps it when a delete failed so the next sign-out tries again. This list
  // must not be the thing that destroys that arrangement — dropping it here left a
  // signed-out account's whole database on a real phone in #5453. What that cost, and why
  // the teardown's own wipe has to preserve it too, are in
  // design-docs/android-local-database-engine.md, "Some notes must survive BOTH localStorage
  // wipes".
  //
  // literal must match SQLITE_MIGRATION_TARGETS_KEY in
  // local-database-engine-decisions.ts (the matching test cross-checks the two). Not
  // imported, because this module is deliberately free of every dependency.
  'cs_sqlite_migration_targets',
  // WHICH COACH COPIES of client databases manage mode opened on this device
  // (df-a00755b05133). A JSON array of logical database names. Kept for exactly the reason
  // the entry above is: "Back to my account" closes the copy, so at sign-out it is not open
  // and the native plugin cannot list it, which leaves this note as the only thing that can
  // name the file. The sign-out teardown removes the note once those files are gone, or
  // keeps it when a delete failed so the next sign-out retries.
  //
  // literal must match SQLITE_COACH_COPY_NAMES_KEY in
  // local-database-engine-decisions.ts (the matching test cross-checks the two). Not
  // imported, because this module is deliberately free of every dependency.
  'cs_coach_copy_db_names',
  // THAT PHASE 4 ROLLED THIS DEVICE BACK, and WHICH SQLITE FILES it left behind
  // (#4981 phase 4, #5270). A JSON object holding a re-arm generation and a list of
  // logical database names.
  //
  // Both halves need this entry, for different reasons. The generation is a
  // device-level fact like the engine marker: without it a rolled-back device is
  // Android native, on IndexedDB and inside the rollout band again, so it copies its
  // whole database onto SQLite on the very next page load and rolls back again on the
  // one after. The names are the ONLY thing left that can identify the superseded
  // SQLite files — the engine marker already says `idb`, so the sign-out deleter is
  // disarmed, and the native plugin has no "list the databases" call. Dropping this
  // key therefore leaves a full duplicate of the signed-out account's data on the
  // phone for ever, which is exactly what dropping `cs_sqlite_migration_targets` cost
  // in #5453.
  //
  // Like the entries above, carrying it across is NOT keeping it for ever: the names
  // are removed by the cleanup that consumed them, and the generation is lifted by a
  // re-arm, which is a COMPARISON against the config key — never a deletion of this
  // key. This list must not be the thing that destroys either decision.
  //
  // literal must match SQLITE_MIGRATION_ROLLED_BACK_KEY in
  // local-database-engine-decisions.ts (the matching test cross-checks the two). Not
  // imported, because this module is deliberately free of every dependency.
  'cs_sqlite_migration_rolled_back',
  // THE ROLLBACK'S COMMIT POINT: what a reverse copy has verified and what still has
  // to be finished (#4981 phase 4, #5270). A JSON object holding, per base database
  // name, the generation the device must move to and the SQLite database to delete
  // once it has.
  //
  // While this value exists the rollback's tail is unfinished and the next boot
  // finishes it. Losing it mid-tail is the worst outcome in this whole area: the
  // device is left reading one engine while a verified copy of its data sits at a
  // generation nothing names, with the sign-out deleter already disarmed. Every step
  // after the record is idempotent precisely so the tail can be re-run — and it can
  // only be re-run if this survives.
  //
  // literal must match SQLITE_ROLLBACK_COMPLETION_KEY in
  // local-database-engine-decisions.ts (the matching test cross-checks the two).
  'cs_sqlite_rollback_completion',
  // HOW MANY SUCCESSFUL BOOTS this device has run on SQLite (#4981 phase 4, #5270). The
  // grace period before the old IndexedDB copy is deleted. NOT "consecutive": a boot that
  // wedges before Ready simply does not count, and nothing resets the counter because of it.
  //
  // **Surviving this wipe and being reset on sign-out are not a contradiction, and both
  // halves have to stay.** This list exists so a PARTIAL teardown — the bounded one that
  // may die before it finishes, or the fatal-error reset — does not silently zero the
  // counter and hand the device a fresh grace period it has already served. A COMPLETED
  // sign-out zeroes it deliberately, as the last thing it does, because the next person's
  // device has served no grace period either. Delete this entry and a device that survives
  // a half-finished teardown starts its ten boots again; delete the sign-out reset and one
  // account's grace period is inherited by the next.
  //
  // literal must match SQLITE_READY_BOOTS_KEY in local-database-engine-decisions.ts (the
  // matching test cross-checks the two).
  'cs_sqlite_ready_boots',
];

// prefixes of our OWN device-level keys, whose contents are per-availableVersion
// or per-device rather than per-user. Unlike the SDK prefixes below, BOTH sign-out
// wipes keep these: the reload teardown's second wipe (reload-teardown.ts) reads
// this list too, so a prefix added here survives the whole sign-out.
export const DEVICE_LEVEL_LOCALSTORAGE_PREFIXES: readonly string[] = [
  // In-app update prompt: per-availableVersion dismissal stamps written by
  // VersionCheckService. The key is `update_prompt_dismissed:${version}` and
  // the value an ISO timestamp. It records "this browser saw and dismissed
  // the prompt for version X" — a device-installation property, not a
  // user-identity property — so it survives signOut. Without preservation,
  // signing out and back in would re-show the same dismissed version's
  // prompt on the next dashboard refresh. See
  // design-docs/in-app-update-prompt.md §Dismiss Policy.
  'update_prompt_dismissed:',
];

// prefixes the FIRST sign-out wipe keeps: third-party SDKs that manage their
// own lifecycle, plus our device-level prefixes above. yanking state out from
// under Firebase Auth mid-signOut, or under Mixpanel between sessions, would
// force re-init and risk losing in-flight events. SDK signOut (called at the
// end of signOutAsync) handles its own cleanup. The reload teardown's second
// wipe still removes the SDK prefixes on purpose — see reload-teardown.ts.
export const PRESERVED_LOCALSTORAGE_PREFIXES: readonly string[] = [
  'firebase',  // Firebase Auth (firebase:authUser:*, firebaseLocalStorage*)
  'mp_',       // Mixpanel core (mp_lib, mp_loader, mp_optout, mp_tab_id_*, ...)
  '__mp',      // Mixpanel session-replay + recorder + queue
  'mixpanel',  // Mixpanel bare keys + IDB names
  'sentry',    // Sentry replays / debug-ids (defensive)
  ...DEVICE_LEVEL_LOCALSTORAGE_PREFIXES,
];

// localStorage key for the per-install device identifier sent with FCM token
// registration. lives in this module (next to the allow-list entry above) so the
// key literal and its signOut-survivor registration cannot drift apart.
export const PUSH_DEVICE_ID_KEY = 'pushDeviceId';

// best-effort stable per-install device id for push-token registration (#3714): lets
// the server replace this device's old token entry on re-registration instead of
// appending. on iOS callers prefer the APNs device token; this UUID is the Android
// path and the iOS fallback. it resets on reinstall (localStorage is wiped with the
// app) — the server's 60-day staleness sweep then retires the orphaned entry.
export function getPersistedDeviceId(): string {
  let id = localStorage.getItem(PUSH_DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(PUSH_DEVICE_ID_KEY, id);
  }
  return id;
}

export function isPreservedLocalStorageKey(key: string): boolean {
  if (DEVICE_LEVEL_LOCALSTORAGE_KEYS.includes(key)) return true;
  for (const prefix of PRESERVED_LOCALSTORAGE_PREFIXES) {
    if (key.startsWith(prefix)) return true;
  }
  return false;
}

/**
 * Snapshot device-level + SDK-prefixed keys, clear localStorage, then restore
 * the snapshot. Net effect: every user-scoped key is wiped, every preserved
 * key keeps its current value.
 */
export function clearUserScopedLocalStorage(): void {
  const survivors: Record<string, string> = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    if (!isPreservedLocalStorageKey(key)) continue;
    const value = localStorage.getItem(key);
    if (value !== null) survivors[key] = value;
  }
  localStorage.clear();
  for (const [k, v] of Object.entries(survivors)) localStorage.setItem(k, v);
}
